"use client";

import { c as _c } from "react/compiler-runtime";
import { useRef, useState, useCallback, useContext, useMemo } from "react";
import type { CreateDropConfig } from "@/entities/IDrop";
import CreateDropStormParts from "./CreateDropStormParts";
import { AnimatePresence, motion } from "framer-motion";
import CreateDropContent from "./CreateDropContent";
import CreateCurationDropContent from "./CreateCurationDropContent";
import { useMutation } from "@tanstack/react-query";
import type { ApiWave } from "@/generated/models/ApiWave";
import { ReactQueryWrapperContext } from "../react-query-wrapper/ReactQueryWrapper";
import { commonApiPost } from "@/services/api/common-api";
import type { ApiCreateDropRequest } from "@/generated/models/ApiCreateDropRequest";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { AuthContext } from "../auth/Auth";
import { useKeyPressEvent } from "react-use";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import type { CurationComposerVariant } from "./dropComposer.types";
import { DropMode } from "./dropComposer.types";
import type { DropPrivileges } from "@/hooks/useDropPriviledges";
import { useMyStream } from "@/contexts/wave/MyStreamContext";
import { ProcessIncomingDropType } from "@/contexts/wave/hooks/useWaveRealtimeUpdater";
import { useUnreadDividerOptional } from "@/contexts/wave/UnreadDividerContext";
import { useWave } from "@/hooks/useWave";
import { resolveWaveSubmissionExperience, WaveSubmissionExperience } from "@/helpers/waves/wave-submission-experience.helpers";
import { getMentionedGroupsFromParts } from "@/helpers/waves/drop-group-mentions";
interface CreateDropProps {
  readonly activeDrop: ActiveDropState | null;
  readonly onCancelReplyQuote: () => void;
  readonly onDropAddedToQueue: () => void;
  readonly onAllDropsAdded?: (() => void) | undefined;
  readonly onExitFixedDropMode?: (() => void) | undefined;
  readonly wave: ApiWave;
  readonly dropId: string | null;
  readonly fixedDropMode: DropMode;
  readonly privileges: DropPrivileges;
  readonly curationComposerVariant?: CurationComposerVariant | undefined;
}
export interface DropMutationBody {
  readonly drop: ApiCreateDropRequest;
  readonly dropId: string | null;
  readonly onSuccess?: (() => void) | undefined;
  readonly onError?: ((error: unknown) => void) | undefined;
}
const ANIMATION_DURATION = 0.3;
export default function CreateDrop(t0) {
  const $ = _c(87);
  const {
    activeDrop,
    onCancelReplyQuote,
    onDropAddedToQueue,
    onAllDropsAdded,
    onExitFixedDropMode,
    wave,
    dropId,
    fixedDropMode,
    privileges,
    curationComposerVariant: t1
  } = t0;
  const curationComposerVariant = t1 === undefined ? "default" : t1;
  const {
    setToast
  } = useContext(AuthContext);
  const {
    waitAndInvalidateDrops
  } = useContext(ReactQueryWrapperContext);
  const unreadDividerContext = useUnreadDividerOptional();
  let t2;
  if ($[0] !== onCancelReplyQuote) {
    t2 = () => onCancelReplyQuote();
    $[0] = onCancelReplyQuote;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  useKeyPressEvent("Escape", t2);
  const [isStormMode, setIsStormMode] = useState(false);
  const [drop, setDrop] = useState(null);
  const [dropModeOverride, setDropModeOverride] = useState(null);
  const [curationPrefillSeed, setCurationPrefillSeed] = useState(null);
  const {
    processDropRemoved,
    processIncomingDrop
  } = useMyStream();
  const {
    isMemesWave,
    isCurationWave
  } = useWave(wave);
  const t3 = wave.participation.submission_strategy ?? null;
  let t4;
  if ($[2] !== isCurationWave || $[3] !== isMemesWave || $[4] !== t3) {
    t4 = resolveWaveSubmissionExperience({
      isMemesWave,
      isCurationWave,
      submissionStrategy: t3
    });
    $[2] = isCurationWave;
    $[3] = isMemesWave;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const submissionExperience = t4;
  let t5;
  if ($[6] !== activeDrop || $[7] !== fixedDropMode || $[8] !== wave.chat.authenticated_user_eligible || $[9] !== wave.participation.authenticated_user_eligible) {
    t5 = () => {
      if (fixedDropMode === DropMode.CHAT) {
        return false;
      }
      if (fixedDropMode === DropMode.PARTICIPATION) {
        return true;
      }
      if (wave.chat.authenticated_user_eligible) {
        return false;
      }
      if (wave.participation.authenticated_user_eligible) {
        return true;
      }
      if (activeDrop) {
        return false;
      }
      return false;
    };
    $[6] = activeDrop;
    $[7] = fixedDropMode;
    $[8] = wave.chat.authenticated_user_eligible;
    $[9] = wave.participation.authenticated_user_eligible;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const getDefaultIsDropMode = t5;
  const activeDropScope = activeDrop === null ? "none" : `${activeDrop.action}:${activeDrop.drop.id}:${activeDrop.partId}`;
  const modeScopeKey = `${wave.id}:${fixedDropMode}:${wave.chat.authenticated_user_eligible}:${wave.participation.authenticated_user_eligible}:${activeDropScope}`;
  const modeScopeToken = modeScopeKey;
  const defaultIsDropMode = getDefaultIsDropMode();
  const isDropMode = dropModeOverride?.scopeKey === modeScopeToken ? dropModeOverride.value : defaultIsDropMode;
  const initialCurationUrl = curationPrefillSeed?.scopeKey === modeScopeToken ? curationPrefillSeed.url : null;
  const isCurationDropMode = submissionExperience === WaveSubmissionExperience.CURATION_LEGACY && isDropMode;
  const canMentionAll = wave.wave.authenticated_user_eligible_for_admin === true;
  let t6;
  if ($[11] !== fixedDropMode || $[12] !== setToast || $[13] !== wave.chat.authenticated_user_eligible || $[14] !== wave.participation.authenticated_user_eligible) {
    t6 = newIsDropMode => {
      if (fixedDropMode !== DropMode.BOTH) {
        return false;
      }
      if (newIsDropMode && !wave.participation.authenticated_user_eligible) {
        setToast({
          message: "You are not eligible to drop in this wave",
          type: "error"
        });
        return false;
      }
      if (!newIsDropMode && !wave.chat.authenticated_user_eligible) {
        setToast({
          message: "You are not eligible to chat in this wave",
          type: "error"
        });
        return false;
      }
      return true;
    };
    $[11] = fixedDropMode;
    $[12] = setToast;
    $[13] = wave.chat.authenticated_user_eligible;
    $[14] = wave.participation.authenticated_user_eligible;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const canSwitchDropMode = t6;
  let t7;
  if ($[16] !== canSwitchDropMode || $[17] !== fixedDropMode || $[18] !== modeScopeToken || $[19] !== onExitFixedDropMode) {
    t7 = newIsDropMode_0 => {
      if (!newIsDropMode_0 && fixedDropMode === DropMode.PARTICIPATION && onExitFixedDropMode) {
        setCurationPrefillSeed(null);
        onExitFixedDropMode();
        return;
      }
      if (!canSwitchDropMode(newIsDropMode_0)) {
        return;
      }
      setCurationPrefillSeed(null);
      setDropModeOverride({
        scopeKey: modeScopeToken,
        value: newIsDropMode_0
      });
    };
    $[16] = canSwitchDropMode;
    $[17] = fixedDropMode;
    $[18] = modeScopeToken;
    $[19] = onExitFixedDropMode;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const onDropModeChange = t7;
  let t8;
  if ($[21] !== canSwitchDropMode || $[22] !== modeScopeToken) {
    t8 = url => {
      if (!canSwitchDropMode(true)) {
        return;
      }
      setCurationPrefillSeed({
        scopeKey: modeScopeToken,
        url
      });
      setDropModeOverride({
        scopeKey: modeScopeToken,
        value: true
      });
    };
    $[21] = canSwitchDropMode;
    $[22] = modeScopeToken;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const onSwitchToDropModeWithUrl = t8;
  let t9;
  if ($[24] !== canMentionAll) {
    t9 = partIndex => {
      setDrop(prevDrop => {
        if (!prevDrop) {
          return null;
        }
        const newParts = prevDrop.parts.filter((_, i) => i !== partIndex);
        return {
          ...prevDrop,
          parts: newParts,
          referenced_nfts: prevDrop.referenced_nfts,
          mentioned_users: prevDrop.mentioned_users,
          mentioned_groups: getMentionedGroupsFromParts(newParts, canMentionAll),
          mentioned_waves: prevDrop.mentioned_waves ?? [],
          metadata: prevDrop.metadata
        };
      });
    };
    $[24] = canMentionAll;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const onRemovePart = t9;
  let t10;
  if ($[26] !== curationComposerVariant || $[27] !== processDropRemoved || $[28] !== processIncomingDrop || $[29] !== setToast || $[30] !== submissionExperience) {
    t10 = (serverDrop, body_0) => {
      if (body_0.dropId) {
        processDropRemoved(body_0.drop.wave_id, body_0.dropId);
      }
      processIncomingDrop(serverDrop, ProcessIncomingDropType.DROP_INSERT);
      body_0.onSuccess?.();
      if (submissionExperience === WaveSubmissionExperience.CURATION_LEGACY && curationComposerVariant === "leaderboard") {
        setToast({
          message: "Drop submitted successfully",
          type: "success"
        });
      }
    };
    $[26] = curationComposerVariant;
    $[27] = processDropRemoved;
    $[28] = processIncomingDrop;
    $[29] = setToast;
    $[30] = submissionExperience;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== processDropRemoved || $[33] !== setToast) {
    t11 = (error, body_1) => {
      setTimeout(() => {
        if (body_1.dropId) {
          processDropRemoved(body_1.drop.wave_id, body_1.dropId);
        }
      }, 0);
      setToast({
        message: error instanceof Error ? error.message : String(error),
        type: "error"
      });
      body_1.onError?.(error);
    };
    $[32] = processDropRemoved;
    $[33] = setToast;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== t10 || $[36] !== t11) {
    t12 = {
      mutationFn: _temp,
      onSuccess: t10,
      onError: t11,
      retry: _temp2,
      retryDelay: _temp3
    };
    $[35] = t10;
    $[36] = t11;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  const addDropMutation = useMutation(t12);
  let t13;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = [];
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  const queueRef = useRef(t13);
  const isProcessingRef = useRef(false);
  const hasBatchErrorsRef = useRef(false);
  const inFlightProcessNextDropRef = useRef(null);
  let t14;
  if ($[39] !== addDropMutation || $[40] !== onAllDropsAdded || $[41] !== waitAndInvalidateDrops) {
    t14 = async () => {
      if (isProcessingRef.current || queueRef.current.length === 0) {
        return;
      }
      isProcessingRef.current = true;
      while (queueRef.current.length > 0) {
        const dropRequest = queueRef.current.shift();
        if (dropRequest === undefined) {
          break;
        }
        ;
        try {
          await addDropMutation.mutateAsync(dropRequest);
        } catch (t15) {
          const error_0 = t15;
          hasBatchErrorsRef.current = true;
          console.error("Error processing drop:", error_0);
        }
      }
      isProcessingRef.current = false;
      const shouldNotifyAllDropsAdded = !hasBatchErrorsRef.current;
      hasBatchErrorsRef.current = false;
      waitAndInvalidateDrops();
      if (shouldNotifyAllDropsAdded) {
        onAllDropsAdded?.();
      }
    };
    $[39] = addDropMutation;
    $[40] = onAllDropsAdded;
    $[41] = waitAndInvalidateDrops;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  const processNextDrop = t14;
  let t15;
  if ($[43] !== onDropAddedToQueue || $[44] !== processNextDrop || $[45] !== unreadDividerContext) {
    t15 = dropRequest_0 => {
      queueRef.current.push(dropRequest_0);
      inFlightProcessNextDropRef.current = processNextDrop();
      if (unreadDividerContext) {
        unreadDividerContext.setUnreadDividerSerialNo(null);
      }
      onDropAddedToQueue();
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    };
    $[43] = onDropAddedToQueue;
    $[44] = processNextDrop;
    $[45] = unreadDividerContext;
    $[46] = t15;
  } else {
    t15 = $[46];
  }
  const submitDrop = t15;
  const hasExitFixedDropMode = onExitFixedDropMode !== undefined;
  const t16 = fixedDropMode === DropMode.BOTH || fixedDropMode === DropMode.PARTICIPATION && hasExitFixedDropMode;
  const t17 = fixedDropMode === DropMode.PARTICIPATION && hasExitFixedDropMode ? "Close create drop" : null;
  const t18 = fixedDropMode === DropMode.BOTH && privileges.chatRestriction === null || fixedDropMode === DropMode.PARTICIPATION && hasExitFixedDropMode;
  let t19;
  if ($[47] !== activeDrop || $[48] !== drop || $[49] !== dropId || $[50] !== isDropMode || $[51] !== isStormMode || $[52] !== onCancelReplyQuote || $[53] !== onDropModeChange || $[54] !== onSwitchToDropModeWithUrl || $[55] !== privileges || $[56] !== submitDrop || $[57] !== t16 || $[58] !== t17 || $[59] !== t18) {
    t19 = {
      activeDrop,
      onCancelReplyQuote,
      drop,
      isStormMode,
      isDropMode,
      dropId,
      setDrop,
      setIsStormMode,
      onDropModeChange,
      onSwitchToDropModeWithUrl,
      submitDrop,
      privileges,
      showDropModeToggle: t16,
      dropModeToggleExitLabel: t17,
      canExitDropMode: t18
    };
    $[47] = activeDrop;
    $[48] = drop;
    $[49] = dropId;
    $[50] = isDropMode;
    $[51] = isStormMode;
    $[52] = onCancelReplyQuote;
    $[53] = onDropModeChange;
    $[54] = onSwitchToDropModeWithUrl;
    $[55] = privileges;
    $[56] = submitDrop;
    $[57] = t16;
    $[58] = t17;
    $[59] = t18;
    $[60] = t19;
  } else {
    t19 = $[60];
  }
  const createDropContentProps = t19;
  let t20;
  if ($[61] !== drop?.mentioned_groups || $[62] !== drop?.mentioned_users || $[63] !== drop?.mentioned_waves || $[64] !== drop?.parts || $[65] !== drop?.referenced_nfts || $[66] !== isCurationDropMode || $[67] !== isStormMode || $[68] !== onRemovePart) {
    t20 = isStormMode && !isCurationDropMode && <motion.div initial={{
      opacity: 0,
      height: 0
    }} animate={{
      opacity: 1,
      height: "auto"
    }} exit={{
      opacity: 0,
      height: 0
    }} transition={{
      duration: ANIMATION_DURATION
    }}><CreateDropStormParts parts={drop?.parts ?? []} mentionedUsers={drop?.mentioned_users ?? []} mentionedGroups={drop?.mentioned_groups ?? []} mentionedWaves={drop?.mentioned_waves ?? []} referencedNfts={drop?.referenced_nfts ?? []} onRemovePart={onRemovePart} /></motion.div>;
    $[61] = drop?.mentioned_groups;
    $[62] = drop?.mentioned_users;
    $[63] = drop?.mentioned_waves;
    $[64] = drop?.parts;
    $[65] = drop?.referenced_nfts;
    $[66] = isCurationDropMode;
    $[67] = isStormMode;
    $[68] = onRemovePart;
    $[69] = t20;
  } else {
    t20 = $[69];
  }
  let t21;
  if ($[70] !== t20) {
    t21 = <AnimatePresence>{t20}</AnimatePresence>;
    $[70] = t20;
    $[71] = t21;
  } else {
    t21 = $[71];
  }
  let t22;
  if ($[72] !== activeDrop || $[73] !== createDropContentProps || $[74] !== curationComposerVariant || $[75] !== dropId || $[76] !== initialCurationUrl || $[77] !== isCurationDropMode || $[78] !== isDropMode || $[79] !== onCancelReplyQuote || $[80] !== submissionExperience || $[81] !== submitDrop || $[82] !== wave) {
    t22 = isCurationDropMode ? <CreateCurationDropContent activeDrop={activeDrop} onCancelReplyQuote={onCancelReplyQuote} wave={wave} dropId={dropId} isDropMode={isDropMode} initialUrl={initialCurationUrl} submitDrop={submitDrop} curationComposerVariant={curationComposerVariant} /> : <CreateDropContent {...createDropContentProps} wave={wave} submissionExperience={submissionExperience} />;
    $[72] = activeDrop;
    $[73] = createDropContentProps;
    $[74] = curationComposerVariant;
    $[75] = dropId;
    $[76] = initialCurationUrl;
    $[77] = isCurationDropMode;
    $[78] = isDropMode;
    $[79] = onCancelReplyQuote;
    $[80] = submissionExperience;
    $[81] = submitDrop;
    $[82] = wave;
    $[83] = t22;
  } else {
    t22 = $[83];
  }
  let t23;
  if ($[84] !== t21 || $[85] !== t22) {
    t23 = <>{t21}{t22}</>;
    $[84] = t21;
    $[85] = t22;
    $[86] = t23;
  } else {
    t23 = $[86];
  }
  return t23;
}
function _temp3(failureCount_0) {
  return failureCount_0 * 1000;
}
function _temp2(failureCount) {
  return failureCount < 3;
}
async function _temp(body) {
  return commonApiPost({
    endpoint: "drops",
    body: body.drop
  });
}